import { useState } from 'react';
import {
  decideExpense,
  decideReport,
  type ReportDecision,
  type RowDecision,
} from './api-decisions';
import type { Outcome } from './api';
import type { Report } from './types';

const KNOWN = [
  'OWN_REPORT',
  'SAME_APPROVER',
  'NOT_YOUR_TURN',
  'EXPENSE_REJECTED',
  'NOTE_REQUIRED',
  'NOT_LATE_DOCUMENT',
  'OWNER_TAX_CODE_MISSING',
] as const;

/** What the page says: a problem code it has a sentence for, or the two general failures. */
export type DecisionError = (typeof KNOWN)[number] | 'conflict' | 'other';

function errorOf(status: number, code: string): DecisionError {
  const known = KNOWN.find((candidate) => candidate === code);
  if (known) return known;
  return status === 409 || status === 412 ? 'conflict' : 'other';
}

/** The approver's decisions on a report and on its rows; each answers with the report. */
export function useDecisions(report: Report, adopt: (report: Report) => void, reload: () => void) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<DecisionError | null>(null);
  const [done, setDone] = useState<ReportDecision | null>(null);

  /** True when the server took the decision; the caller keeps what was typed otherwise. */
  const run = async (
    act: () => Promise<Outcome<Report>>,
    kind: ReportDecision | null,
  ): Promise<boolean> => {
    setBusy(true);
    setError(null);
    setDone(null);
    const outcome = await act();
    setBusy(false);
    if (outcome.ok) {
      adopt(outcome.value);
      setDone(kind);
      return true;
    }
    const failure = errorOf(outcome.status, outcome.code);
    setError(failure);
    // A stale version, a turn that has passed or a tax code just removed: read the report again,
    // so the page is true.
    if (
      failure === 'conflict' ||
      failure === 'NOT_YOUR_TURN' ||
      failure === 'OWNER_TAX_CODE_MISSING'
    )
      reload();
    return false;
  };

  return {
    busy,
    error,
    done,
    decide: (kind: ReportDecision, note: string) =>
      run(() => decideReport(kind, report.id, report.version, note), kind),
    decideRow: (kind: RowDecision, expenseId: string, note: string) =>
      run(() => decideExpense(kind, report.id, expenseId, report.version, note), null),
  } as const;
}
